import type { MaybeRefOrGetter } from 'vue';
import type { GlobalCollectionId } from '#layers/data-model/public';
import { computed, toValue } from 'vue';
import { useAuth } from '#layers/access/public';
import { isGeneratedKey } from '#layers/base/public';
import { isBlockSerialized, useBlockContext } from '#layers/block-engine/public';

export interface UseBlockStorageKeyOptions {
	/**
	 * Optional collection ID to include in the storage key.
	 */
	collectionId?: MaybeRefOrGetter<GlobalCollectionId | null>;
	/**
	 * Determines the scoping of the state.
	 *
	 * 'user' -- The key includes the current user's ID
	 * 'global' -- The key does not include the user ID
	 */
	scope?: 'user' | 'global';
}

/**
 * Generates a reactive storage key scoped by block context, user, and collection details.
 *
 * @param {MaybeRefOrGetter<string>} key - The base key used for identifying the storage.
 * @param {UseBlockStorageKeyOptions} [options] - Optional configuration for scoping.
 * @return An object containing the computed storage key and individual key components.
 */
export function useBlockStorageKey(key: MaybeRefOrGetter<string>, options?: UseBlockStorageKeyOptions) {
	const { getChild, getRootBlock, getRootContext } = useBlockContext();
	const { currentUser } = useAuth();

	const rootBlockId = computed(() => {
		const rootBlock = getRootBlock();

		if (isBlockSerialized(rootBlock) && !isGeneratedKey(rootBlock.id)) {
			return rootBlock.id;
		}

		return null;
	});

	const childBlockId = computed(() => getChild().id);
	const collectionId = computed(() => toValue(options?.collectionId) ?? getRootContext().collectionId ?? null);
	const ownerId = computed(() => options?.scope === 'user' ? currentUser.value?.id ?? null : null);

	const storageKey = computed(() =>
		[key, rootBlockId, collectionId, childBlockId, ownerId].map((k) => toValue(k)).filter(Boolean).join(':'),
	);

	return {
		storageKey,
		rootBlockId,
		childBlockId,
		collectionId,
		ownerId,
	};
}
